import {
  useMutation,
  UseMutationResult,
  useQuery,
  useQueryClient,
  UseQueryOptions,
} from '@tanstack/react-query';
import axios, { AxiosError } from 'axios';

import { APIV3ErrorData } from '@dbt-labs/app-nav';

import { API_V3, ApiV3Response, PrivateApiResponse } from './types';

export interface FavoriteAsset {
  id: number;
  account_id: number;
  asset_id: string;
  environment_id?: string;
  project_id?: number;
  name?: string;
  platform?: string;
  asset_type?: string;
}

export const getFavoriteAssets = async (
  accountId: number,
): Promise<FavoriteAsset[]> => {
  const response = await axios.get<PrivateApiResponse<FavoriteAsset[]>>(
    `${API_V3}/accounts/${accountId}/favorite-assets/`,
    {},
  );

  return response?.data?.data;
};

export const useFavoriteAssets = (
  accountId?: number,
  options?: Omit<
    UseQueryOptions<FavoriteAsset[], AxiosError>,
    'queryKey' | 'queryFn' | 'enabled'
  >,
) => {
  return useQuery<FavoriteAsset[], AxiosError>({
    queryKey: ['favorite-assets', accountId],
    queryFn: () => getFavoriteAssets(accountId ?? 0),
    enabled: !!accountId,
    ...options,
  });
};

type AddFavoriteAssetVariables = {
  accountId: number;
  projectId?: number;
  assetId?: string;
  name?: string;
  platform?: string;
  assetType?: string;
  environmentId?: string;
};

export const addFavoriteAsset = async ({
  accountId,
  projectId,
  assetId,
  name,
  platform,
  assetType,
  environmentId,
}: AddFavoriteAssetVariables) => {
  const response = await axios.post<PrivateApiResponse<FavoriteAsset>>(
    `${API_V3}/accounts/${accountId}/favorite-assets/`,
    {
      project_id: projectId,
      asset_id: assetId,
      name,
      platform,
      asset_type: assetType,
      environment_id: environmentId,
    },
  );

  return response?.data?.data;
};

export const useAddFavoriteAsset = (
  shouldInvalidate: boolean = true,
): UseMutationResult<
  FavoriteAsset,
  AxiosError<ApiV3Response<APIV3ErrorData>>,
  AddFavoriteAssetVariables
> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      accountId,
      projectId,
      assetId,
      name,
      platform,
      assetType,
      environmentId,
    }) =>
      addFavoriteAsset({
        accountId,
        projectId,
        assetId,
        name,
        platform,
        assetType,
        environmentId,
      }),
    onSettled: (_, __, vars) => {
      if (vars?.accountId && shouldInvalidate) {
        queryClient.invalidateQueries({
          queryKey: ['favorite-assets', vars.accountId],
        });
      }
    },
  });
};

type DeleteFavoriteAssetVariables = {
  accountId: number;
  assetId?: string;
  projectId?: number;
  environmentId?: string;
};

export const deleteFavoriteAsset = async ({
  accountId,
  projectId,
  assetId,
  environmentId,
}: DeleteFavoriteAssetVariables): Promise<FavoriteAsset> => {
  const response = await axios.delete<PrivateApiResponse<FavoriteAsset>>(
    `${API_V3}/accounts/${accountId}/favorite-assets/`,
    {
      data: { project_id: projectId, asset_id: assetId, environment_id: environmentId },
    },
  );

  return response?.data?.data;
};

export const useDeleteFavoriteAsset = (
  shouldInvalidate: boolean = true,
): UseMutationResult<
  FavoriteAsset,
  AxiosError<ApiV3Response<APIV3ErrorData>>,
  DeleteFavoriteAssetVariables
> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ accountId, projectId, assetId, environmentId }) =>
      deleteFavoriteAsset({ accountId, projectId, assetId, environmentId }),
    onSettled: (_, __, vars) => {
      if (vars?.accountId && shouldInvalidate) {
        queryClient.invalidateQueries({
          queryKey: ['favorite-assets', vars.accountId],
        });
      }
    },
  });
};
